import { Building2 } from 'lucide-vue-next';
import { computed } from 'vue';

import { useSidebar } from '@/composables/use-sidebar';
import router from '@/router';
import { useAuthStore } from '@/stores/auth';
import { useOrganizationStore } from '@/stores/organization';
import { useRingCentralStore } from '@/stores/ringcentral';

import type { SidebarData, Team, User } from '../types';

// 使用计算属性创建用户信息，这样当 authStore 中的数据更新时，用户信息会自动更新
const user = computed<User>(() => {
  // 使用 Pinia store 获取用户信息 (inside computed to ensure proper initialization)
  const authStore = useAuthStore();

  // 如果用户已登录，使用存储中的用户信息
  if (authStore.isLogin && authStore.user) {
    return {
      name: authStore.user.email.split('@')[0] || 'User',
      email: authStore.user.email || '',
      avatar: '', // Empty string will trigger the avatar fallback
    };
  }

  // If not logged in, redirect and return minimal user info
  router.push('/auth/sign-in');
  return {
    name: 'Guest',
    email: '',
    avatar: '',
  };
});

// Generate teams based on available organizations
const teams = computed<Team[]>(() => {
  // Create teams from organization data (inside computed to ensure proper initialization)
  const orgStore = useOrganizationStore();
  const ringCentralStore = useRingCentralStore();

  // Combine memberships from main API and RingCentral organizations
  const membershipTeams: Team[] = [];
  const ringCentralTeams: Team[] = [];

  // Add teams from main API memberships
  if (
    orgStore.memberships &&
    Array.isArray(orgStore.memberships) &&
    orgStore.memberships.length > 0
  ) {
    membershipTeams.push(
      ...orgStore.memberships.map(org => {
        const plan = org.role === 'admin' ? 'Enterprise' : 'Standard';
        return {
          id: org.orgId,
          name: org.orgName,
          logo: Building2,
          plan,
          source: 'main-api' as const,
        };
      }),
    );
  }

  // Add teams from RingCentral organizations
  if (
    ringCentralStore.organizations &&
    Array.isArray(ringCentralStore.organizations) &&
    ringCentralStore.organizations.length > 0
  ) {
    ringCentralTeams.push(
      ...ringCentralStore.organizations.map(org => {
        return {
          id: org.organizationId,
          name: org.name,
          logo: Building2,
          plan: 'RingCentral',
          source: 'ringcentral-api' as const,
          metadata: org.metadata,
        };
      }),
    );
  }

  // Combine both sources, avoiding duplicates by orgId
  const allTeams = [...membershipTeams, ...ringCentralTeams];
  const uniqueTeams = allTeams.filter(
    (team, index, array) => array.findIndex(t => t.id === team.id) === index,
  );

  // Sort teams by last used timestamp (most recently used first)
  const timestamps = orgStore.lastUsedTimestamps;
  const sortedTeams = [...uniqueTeams].sort((a, b) => {
    const timeA = (a.id && timestamps[a.id]) || 0;
    const timeB = (b.id && timestamps[b.id]) || 0;
    // Sort in descending order (most recent first)
    return timeB - timeA;
  });

  // Return sorted teams (can be empty array if no organizations)
  return sortedTeams;
});

// 创建响应式的 sidebarData
export const sidebarData = computed<SidebarData>(() => {
  // Get sidebar navigation data (inside computed to ensure proper initialization)
  const { navData } = useSidebar();

  return {
    user: user.value,
    teams: teams.value,
    navMain: navData.value!,
  };
});
